import { ChevronDown } from 'lucide-react'
import type { Faq } from '@/data/faqs'

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function Highlight({ text, query }: { text: string; query: string }) {
  const words = query
    .trim()
    .split(/\s+/)
    .map(escapeRegExp)
    .filter(w => w.length > 1)
  if (words.length === 0) return <>{text}</>

  const splitter = new RegExp(`(${words.join('|')})`, 'gi')
  const tester = new RegExp(`^(${words.join('|')})$`, 'i')

  return (
    <>
      {text.split(splitter).map((part, i) =>
        tester.test(part) ? (
          <mark key={i} className="rounded-sm bg-signal-light px-0.5 text-ink">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  )
}

function FaqList({ items, highlight = '' }: { items: Faq[]; highlight?: string }) {
  return (
    <div className="space-y-3">
      {items.map(item => (
        <details key={item.q} className="card group open:border-blueprint-300">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-2xl px-5 py-4 text-left text-lg font-bold [&::-webkit-details-marker]:hidden">
            <span className="text-ink">
              <Highlight text={item.q} query={highlight} />
            </span>
            <ChevronDown
              aria-hidden="true"
              className="h-5 w-5 shrink-0 text-blueprint-600 transition-transform group-open:rotate-180"
            />
          </summary>
          <p className="px-5 pb-5 text-steel-700">
            <Highlight text={item.a} query={highlight} />
          </p>
        </details>
      ))}
    </div>
  )
}

export function faqSchema(items: Faq[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(i => ({
      '@type': 'Question',
      name: i.q,
      acceptedAnswer: { '@type': 'Answer', text: i.a },
    })),
  }
}

export default FaqList
